import { call, put, all } from 'redux-saga/effects';
import { appendSelectData } from '../select-pagination/select-pagination-actions';
import domainService from '../../../services/modules/domain/domain-service';
import { formatMessage } from '~/i18n/translate';

const LIMIT = 10;

export interface SelectConfig {
  key: string;
  externalCodes: number[];
  currentItems: any[];
  service: any;
}
export function* ensureSelectData(configs: SelectConfig[]) {
  const result: Record<string, any[]> = {};

  for (const cfg of configs) {
    const { key, externalCodes = [], currentItems, service } = cfg;

    if (!externalCodes.length) {
      result[key] = [];
      continue;
    }

    const currentMap = new Map(currentItems.map(item => [item.externalCode, item]));

    const missingCodes = externalCodes.filter(code => !currentMap.has(code));

    let fetchedMap = new Map<number, any>();

    if (missingCodes.length) {
      const { data } = yield call(service, undefined, undefined, missingCodes);

      fetchedMap = new Map(data.map(item => [item.externalCode, item]));
    }

    result[key] = externalCodes.map(
      code =>
        currentMap.get(code) ||
        fetchedMap.get(code) || { externalCode: code, name: formatMessage('commonAutocompleteSelectNoOptionsName') }
    );
  }

  return result;
}

export function* loadDomainSelects(limit: number = LIMIT) {
  const [
    { data: providers },
    { data: departments },
    { data: sections },
    { data: brands },
    { data: categories },
    { data: productLines }
  ] = yield all([
    call(domainService.getProviders, limit),
    call(domainService.getDepartments, limit),
    call(domainService.getSections, limit),
    call(domainService.getBrands, limit),
    call(domainService.getCategories, limit),
    call(domainService.getProductLines, limit)
  ]);

  yield all([
    put(appendSelectData('domain/providers', providers, providers.length === limit)),
    put(appendSelectData('domain/departments', departments, departments.length === limit)),
    put(appendSelectData('domain/sections', sections, sections.length === limit)),
    put(appendSelectData('domain/brands', brands, brands.length === limit)),
    put(appendSelectData('domain/categories', categories, categories.length === limit)),
    put(appendSelectData('domain/product-lines', productLines, productLines.length === limit))
  ]);

  return { providers, departments, sections, brands, categories, productLines };
}

export function* ensureRestrictions(productRestrictions: any, current: any) {
  return yield* ensureSelectData([
    {
      key: 'providers',
      externalCodes: productRestrictions?.providerExternalCode,
      currentItems: current.providers,
      service: domainService.postProviders.bind(domainService)
    },
    {
      key: 'brands',
      externalCodes: productRestrictions?.brandExternalCode,
      currentItems: current.brands,
      service: domainService.getBrands.bind(domainService)
    },
    {
      key: 'departments',
      externalCodes: productRestrictions?.departmentExternalCode,
      currentItems: current.departments,
      service: domainService.getDepartments.bind(domainService)
    },
    {
      key: 'sections',
      externalCodes: productRestrictions?.sectionExternalCode,
      currentItems: current.sections,
      service: domainService.getSections.bind(domainService)
    },
    {
      key: 'categories',
      externalCodes: productRestrictions?.categoryExternalCode,
      currentItems: current.categories,
      service: domainService.getCategories.bind(domainService)
    },
    {
      key: 'productLines',
      externalCodes: productRestrictions?.productLineExternalCode,
      currentItems: current.productLines,
      service: domainService.getProductLines.bind(domainService)
    }
  ]);
}
